import { createReducer } from "@reduxjs/toolkit";

import {
  getListDonations,
  getListDonationsFailure,
  getListDonationsSuccess,
  getListSubscriptions,
  getListSubscriptionsFailure,
  getListSubscriptionsSuccess,
  getListContentRequests,
  getListContentRequestsSuccess,
  getListContentRequestsFailure,
  getPaymentSubscriptions,
  getPaymentSubscriptionsFailure,
  getPaymentSubscriptionsSuccess,
  getPortionPaymentSubscriptions,
  getPortionPaymentSubscriptionsFailure,
  getPortionPaymentSubscriptionsSuccess,
  resetPaymentsList,
  cancelSubscriptionReset,
  cancelSubscriptionSuccess,
  submitTransferSuccess,
  submitTransferReset,
  submitTransferFailure,
  cryptoTx,
  getPromotions,
  getPromotionsSuccess,
  getPromotionsFailure,
  addPayoutMethod,
  addPayoutMethodSuccess,
  addPayoutMethodFailure,
  sendDonation,
  sendDonationSuccess,
  sendDonationFailure,
  getAgency,
  getAgencySuccess,
  getAgencyFailure,
  getAgencyUsers,
  getAgencyUsersSuccess,
  getAgencyUsersFailure,
} from "./actions";

/**
 * @property {number[]} allPayments array of post ids
 * @property {object[]} paymentsById { [postId]: {postContent} }
 */
const initialState = {
  allPayments: [],
  paymentsById: {},
  promotionTransactionsById: {},
  pendingPayments: [],
  totalCount: undefined,
  isLoading: false,
  isPaymentProcessing: false,
  error: null,
  isSuccess: false,
  users: [],
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(getPaymentSubscriptions, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(getPaymentSubscriptionsSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      error: initialState.error,
      isLoading: false,
    }))
    .addCase(getPaymentSubscriptionsFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(getPortionPaymentSubscriptions.type, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(
      getPortionPaymentSubscriptionsSuccess.type,
      (state, { payload }) => {
        const { allPayments, paymentsById } = state;
        const {
          allPayments: newPayments,
          paymentsById: newPaymentById,
          totalCount,
          nextOffset,
        } = payload;

        return {
          ...state,
          allPayments: [...allPayments, ...newPayments],
          paymentsById: { ...paymentsById, ...newPaymentById },
          totalCount,
          nextOffset,
          error: initialState.error,
          isLoading: false,
        };
      },
    )
    .addCase(getPortionPaymentSubscriptionsFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isLoading: false,
    }))
    .addCase(getListSubscriptions, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(getListSubscriptionsSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      error: initialState.error,
      isLoading: false,
    }))
    .addCase(getListSubscriptionsFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(getListContentRequests, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(getListContentRequestsSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      error: initialState.error,
      isLoading: false,
    }))
    .addCase(getListContentRequestsFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(getListDonations, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(getListDonationsSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      error: initialState.error,
      isLoading: false,
    }))
    .addCase(getListDonationsFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(resetPaymentsList.type, () => initialState)
    .addCase(cancelSubscriptionSuccess.type, (state, { payload }) => {
      return {
        ...state,
        paymentsById: {
          ...state.paymentsById,
          [payload]: {
            ...state.paymentsById[payload],
            status: 2,
            isSubscriptionDeleting: true,
          },
        },
        isLoading: false,
      };
    })
    .addCase(cancelSubscriptionReset, (state, { payload }) => {
      return {
        ...state,
        paymentsById: {
          ...state.paymentsById,
          [payload]: {
            ...state.paymentsById[payload],
            isSubscriptionDeleting: false,
          },
        },
        isLoading: false,
      };
    })
    .addCase(cryptoTx.type, (state) => ({
      ...state,
      isPaymentProcessing: true,
    }))
    .addCase(submitTransferFailure.type, (state) => ({
      ...state,
      isPaymentProcessing: false,
    }))
    .addCase(submitTransferSuccess.type, (state, { payload }) => ({
      ...state,
      isPaymentProcessing: false,
      pendingPayments: [payload],
    }))
    .addCase(submitTransferReset.type, (state) => ({
      ...state,
      isPaymentProcessing: false,
      pendingPayments: [],
    }))
    .addCase(getPromotions, (state) => ({
      ...state,
      error: initialState.error,
      isLoading: true,
    }))
    .addCase(addPayoutMethod, (state) => ({
      ...state,
      isSuccess: false,
      isLoading: true,
    }))
    .addCase(addPayoutMethodSuccess, (state) => ({
      ...state,
      isSuccess: true,
      isLoading: false,
    }))
    .addCase(addPayoutMethodFailure, (state) => ({
      ...state,
      isSuccess: false,
      isLoading: false,
    }))
    .addCase(getPromotionsSuccess.type, (state, { payload }) => ({
      ...state,
      ...payload,
      isLoading: false,
    }))
    .addCase(getPromotionsFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(sendDonation.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(sendDonationSuccess.type, (state, { payload }) => ({
      ...state,
      ...payload,
      isLoading: false,
    }))
    .addCase(sendDonationFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(getAgency, (state) => ({
      ...state,
      error: initialState.error,
      isLoading: true,
    }))
    .addCase(getAgencySuccess.type, (state, { payload }) => ({
      ...state,
      ...payload,
      isLoading: false,
    }))
    .addCase(getAgencyFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }))
    .addCase(getAgencyUsers, (state) => ({
      ...state,
      error: initialState.error,
      isLoading: true,
    }))
    .addCase(getAgencyUsersSuccess.type, (state, { payload }) => ({
      ...state,
      users: payload,
      isLoading: false,
    }))
    .addCase(getAgencyUsersFailure.type, (state) => ({
      ...state,
      isLoading: false,
    }));
});
